import {Form, Formik, FormikProps} from 'formik';
import {forEach, isEqual, map} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {StepWizardChildProps} from 'react-step-wizard';
import * as Yup from 'yup';

import {DataWrapper, InputFormik, SelectFormik, WizardFooter} from 'components';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, BillingAddress, BillingPlatformCustomer, WizardFooterProps} from 'types';
import {apiUtils, countryUtils} from 'utils';

const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const T_KEYS = TRANSLATION_KEYS.COMPONENTS.CHARGEBEE_BILLING;

type FormTemplateProps = {
    error: ApiError;
    onChange?: (value: BillingAddress) => void;
} & FormikProps<BillingAddress>;

const ZIP_MIN_LENGTH = 4;
const FIELDS = {
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    LINE1: 'line1',
    LINE2: 'line2',
    CITY: 'city',
    STATE: 'state',
    ZIP: 'zip',
    COUNTRY: 'country'
};

const FormTemplate: React.FC<FormTemplateProps> = ({values, error, setFieldError, onChange, handleSubmit}) => {
    const {t} = useTranslation();

    const countryOptions = useMemo(() => countryUtils.getCountryOptions(), []);

    useEffect(() => {
        const formErrors = apiUtils.getFieldsValidationErrorMessages(error, t, map(FIELDS, (field) => ({field})));
        forEach(formErrors, (value, key) => value && setFieldError(key, value));
    }, [error, t, setFieldError]);

    useEffect(() => {
        onChange?.(values);
    }, [values, onChange]);

    return (
        <Form onSubmit={handleSubmit}>
            <div className='tw-ts-h5-bold tw-mt-8'>{t(T_KEYS.TITLE)}</div>
            <div className='md:tw-grid tw-grid-cols-2 tw-gap-4'>
                <InputFormik
                    name={FIELDS.FIRST_NAME}
                    label={t(T_KEYS_COMMON.SESSION.LABELS.FIRST_NAME)}
                    spacing='top'
                    required
                />
                <InputFormik
                    name={FIELDS.LAST_NAME}
                    label={t(T_KEYS_COMMON.SESSION.LABELS.LAST_NAME)}
                    spacing='top'
                    required
                />
            </div>
            <SelectFormik
                valueAs='id'
                name={FIELDS.COUNTRY}
                className='tw-mt-2'
                isSearchable
                placeholder={t(T_KEYS_COMMON.BILLING_ADDRESS.COUNTRY)}
                options={countryOptions}
                required
            />
            <InputFormik name={FIELDS.LINE1} label={t(T_KEYS_COMMON.BILLING_ADDRESS.ADDRESS_LINE_1)} spacing='top' required />
            <InputFormik name={FIELDS.LINE2} label={t(T_KEYS_COMMON.BILLING_ADDRESS.ADDRESS_LINE_2)} spacing='top' />
            <div className='md:tw-grid tw-grid-cols-3 tw-gap-4'>
                <InputFormik name={FIELDS.CITY} label={t(T_KEYS_COMMON.BILLING_ADDRESS.CITY)} spacing='top' required />
                <InputFormik name={FIELDS.STATE} label={t(T_KEYS_COMMON.BILLING_ADDRESS.STATE)} spacing='top' required />
                <InputFormik name={FIELDS.ZIP} label={t(T_KEYS_COMMON.BILLING_ADDRESS.ZIP)} spacing='top' required />
            </div>
        </Form>
    );
};

type Props = {
    value: BillingAddress;
    onChange?: (address: BillingAddress) => void;
    onSubmit?: (address: BillingAddress) => void;
    updateCallback: (address: BillingAddress) => Promise<any>;
    wizardFooterProps: WizardFooterProps;
} & Partial<StepWizardChildProps>;

export const BillingAddressStep: React.FC<Props> = ({
    value, nextStep, previousStep, wizardFooterProps, updateCallback, onSubmit, onChange
}) => {
    const {t} = useTranslation();

    const handleAddressSave = useCallback((customer: BillingPlatformCustomer) => {
        onSubmit?.(customer.billingAddress);
        nextStep();
    }, [nextStep, onSubmit]);

    const {send: updateBillingAddress, loading, error} = useApiData(updateCallback, {
        onSuccess: handleAddressSave
    });

    const initialValues: BillingAddress = useMemo(() => (value || {
        firstName: '',
        lastName: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    }), [value]);

    const validationSchema = useMemo(() => Yup.object().shape({
        firstName: Yup.string().required(t(T_KEYS_COMMON.ERROR.CODE_10000)),
        lastName: Yup.string().required(t(T_KEYS_COMMON.ERROR.CODE_10000)),
        line1: Yup.string().required(t(T_KEYS_COMMON.ERROR.CODE_10000)),
        city: Yup.string().required(t(T_KEYS_COMMON.ERROR.CODE_10000)),
        state: Yup.string().required(t(T_KEYS_COMMON.ERROR.CODE_10000)),
        zip: Yup.string()
            .required(t(T_KEYS_COMMON.ERROR.CODE_10000))
            .min(ZIP_MIN_LENGTH, t(T_KEYS_COMMON.ERROR.CODE_10001)),
        country: Yup.string().required(t(T_KEYS_COMMON.ERROR.CODE_10000))
    }), [t]);

    const handleSubmit = useCallback((formValues: BillingAddress) => {
        if (isEqual(formValues, value)) {
            nextStep();
        } else {
            updateBillingAddress(formValues);
        }
    }, [nextStep, value, updateBillingAddress]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            isInitialValid={!!value}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                    <FormTemplate {...props} error={error} onChange={onChange} />
                    {error && <div className='tw-mt-4 tw-text-center tw-text-alert tw-text-lg'>{t(T_KEYS.ERROR)}</div>}
                    <WizardFooter
                        {...wizardFooterProps}
                        disabledNext={wizardFooterProps.disabledNext || !props.isValid}
                        handleNext={() => handleSubmit(props.values)}
                        handlePrevious={previousStep}
                    />
                </DataWrapper>
            )}
        </Formik>
    );
};